import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CountryConfig, StationV2, UrlV2 } from "@orvis/core";
import type { FirestoreLike } from "@orvis/data";
import { EmptyRow, LoadingRow, NotLive, StationCard } from "../components/ui.js";
import { StationPlayer } from "../components/players/index.js";
import { PlayerControls } from "../components/players/PlayerControls.js";
import { inlineAdAfter, showInterstitialForChannelTap } from "../ads.js";
import { InlineAd } from "../components/InlineAd.js";
import { BrowseFilters } from "../components/BrowseFilters.js";
import { ReportButton } from "../components/ReportButton.js";
import { ZapButtons } from "../components/ZapButtons.js";
import { CastButton, CastingBanner, PipButton } from "../components/PlayerExtras.js";
import { useChannels } from "../queries.js";
import { useNowPlaying } from "../state/nowPlaying.js";
import { useFavorites } from "../state/favorites.js";
import { useRecentlyPlayed } from "../state/recentlyPlayed.js";
import { useMediaSession } from "../hooks/useMediaSession.js";
import { useBrowseFilter } from "../hooks/useBrowseFilter.js";
import { sameStation, useStationList, zapTarget, type KeyedStation } from "../hooks/useStationList.js";
import { useZapGestures } from "../hooks/useZapGestures.js";
import { usePictureInPicture } from "../hooks/usePictureInPicture.js";
import { useCast } from "../hooks/useCast.js";
import { useTvMode } from "../hooks/useTvMode.js";

export function TvScreen({ db, country }: { db: FirestoreLike; country: CountryConfig }) {
  const { t } = useTranslation();
  const q = useChannels(db, country.code);
  const now = useNowPlaying();
  const favorites = useFavorites();
  const recent = useRecentlyPlayed();
  const [filter, setFilter] = useBrowseFilter();
  const list = useStationList(q.data?.open, filter);

  // Zapping walks the list the user is looking at (current filter, playable
  // only) and skips the interstitial — that's reserved for deliberate taps.
  const zap = useCallback(
    (dir: 1 | -1) => {
      const target = zapTarget(list, now.tv, dir);
      if (!target) return;
      recent.push("tv", target.key);
      now.playTv(target.station);
    },
    [list, now, recent]
  );

  // Media Session — surfaces the current TV station to the lockscreen/notification,
  // with working pause/resume so backgrounding the app doesn't just kill playback.
  useMediaSession(now.tv, {
    paused: now.tvPaused,
    onPause: () => now.pauseTv(),
    onResume: () => now.resumeTv(),
    onStop: () => now.playTv(undefined),
    onNext: () => zap(1),
    onPrev: () => zap(-1),
  });

  if (q.isPending) return <LoadingRow label={t("tv")} />;
  const data = q.data;
  if (!data || data.live === false) return <NotLive />;

  const select = async ({ station, key }: KeyedStation) => {
    if (!station.live) return;
    await showInterstitialForChannelTap(country);
    recent.push("tv", key);
    now.playTv(station);
  };

  return (
    <div className="p-3 space-y-3">
      {now.tv ? (
        <TvPlayerBar
          station={now.tv}
          country={country}
          paused={now.tvPaused}
          onTogglePause={() => (now.tvPaused ? now.resumeTv() : now.pauseTv())}
          onClose={() => now.playTv(undefined)}
          onZap={zap}
        />
      ) : null}
      <BrowseFilters stations={data.open} filter={filter} onChange={setFilter} />
      <div className="space-y-2">
        {list.length === 0 ? <EmptyRow label={t("nothing_here_yet")} /> : null}
        {list.map((item, idx) => (
          <Fragment key={item.key}>
            <StationCard
              station={item.station}
              selected={sameStation(now.tv, item.station)}
              showLiveBadge
              isFavorite={favorites.isFavorite("tv", item.key)}
              onToggleFavorite={() => favorites.toggle("tv", item.key)}
              onSelect={() => void select(item)}
            />
            {inlineAdAfter(idx, list.length) ? <InlineAd country={country} slot="tv" /> : null}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function TvPlayerBar({
  station,
  country,
  paused,
  onTogglePause,
  onClose,
  onZap,
}: {
  station: StationV2;
  country: CountryConfig;
  paused: boolean;
  onTogglePause: () => void;
  onClose: () => void;
  onZap: (dir: 1 | -1) => void;
}) {
  const { t } = useTranslation();
  const videoBox = useRef<HTMLDivElement>(null);
  const [activeUrl, setActiveUrl] = useState<UrlV2 | undefined>();
  const [media, setMedia] = useState<HTMLMediaElement | null>(null);
  const tv = useTvMode();
  useZapGestures(true, () => onZap(1), () => onZap(-1), videoBox);
  // Closing the Android PiP window should stop the video, not leave it
  // playing invisibly.
  const pip = usePictureInPicture(media, !paused, () => {
    if (!paused) onTogglePause();
  });
  const cast = useCast({ station, url: activeUrl, kind: "video", el: media });

  // Scroll the player into view when a new station is selected — small nicety
  // so the list doesn't stay scrolled below the fold on a channel change.
  useEffect(() => {
    document.getElementById("tv-player-bar")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [station]);

  const report = (compact: boolean) => (
    <ReportButton country={country.code} kind="tv" station={station} url={activeUrl?.value} compact={compact} />
  );

  return (
    <div id="tv-player-bar" className="scroll-mt-20 rounded-lg border border-border">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-card rounded-t-lg">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{station.name}</div>
          <div className="text-xs text-text/60">{t("live")}</div>
        </div>
        <ZapButtons onPrev={() => onZap(-1)} onNext={() => onZap(1)} />
        {pip.supported ? <PipButton onClick={pip.enter} /> : null}
        {cast.available ? <CastButton onClick={cast.open} active={cast.casting} device={cast.device} /> : null}
        {report(true)}
        <button onClick={onClose} className="text-sm text-text/70 hover:text-text px-2 py-1">
          {t("go_back")}
        </button>
      </div>
      {cast.casting ? <div className="px-3 bg-card"><CastingBanner device={cast.device} /></div> : null}
      <div ref={videoBox} className={"aspect-video bg-black overflow-hidden " + (tv ? "" : "rounded-b-lg")}>
        <StationPlayer
          station={station}
          mode="video"
          paused={paused || cast.casting}
          nativeControls={!tv}
          onElement={setMedia}
          onActiveUrl={setActiveUrl}
          failedActions={report(false)}
        />
      </div>
      {tv ? (
        <div className="px-3 py-2 bg-card rounded-b-lg">
          <PlayerControls
            media={media}
            paused={paused}
            onTogglePause={onTogglePause}
            fullscreenTarget={videoBox}
            onZap={onZap}
          />
        </div>
      ) : null}
    </div>
  );
}
